import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  SlotClassArgs,
  DateChangeEvent,
  EventStyleArgs,
  Group,
} from '@progress/kendo-angular-scheduler';
import { Day } from '@progress/kendo-date-math';
import { map, shareReplay, startWith, switchMap } from 'rxjs';
import { IKendoSchedulerResource, ISchedulerEvent } from 'src/app/scheduler/models/scheduler';
import { IHoliday } from 'src/app/setting/holiday/model/holiday';
import { RessourceType } from 'src/app/setting/models/ressource';
import { TenantSettingsStoreService } from 'src/app/setting/tenant-settings/services/tenant-settings-store.service';
import { AbsenceState } from 'src/app/time-tracking/models/absence-group';
import { AbsenceGroupStoreService } from 'src/app/time-tracking/storage/absence-group-store.service';
import { WorkTimeSchedule, WorkTimeScheduleService } from '../services/work-time-schedule.service';


@Component({
    selector: 'app-absence-scheduler',
    templateUrl: './absence-scheduler.component.html',
    styleUrls: ['./absence-scheduler.component.css'],
    standalone: false
})
export class AbsenceSchedulerComponent implements OnInit {
  constructor(
    private absenceGroupStore : AbsenceGroupStoreService,
    private tenantSettings: TenantSettingsStoreService,
    private workTimeSchedules: WorkTimeScheduleService
  ) {}

  tenantSettings$ = this.tenantSettings.tenantSettings$;

  holidays: IHoliday[] = [];

  events$ = this.absenceGroupStore.absenceEvents$;
  
  /** Worktime schedules keyed by employee id (the resource id in this scheduler). */
  private workTimesByEmployee = new Map<string, WorkTimeSchedule[]>();

  // Same pensum visualisation as the main planner (scheduler-shared): once the resources are
  // known, load the employees' worktimes and re-emit the resources as a new array so the
  // scheduler re-renders its slots through the slotClass callback.
  resources$ = this.absenceGroupStore.resources$.pipe(
    switchMap((resources) => {
      const employeeIds = (resources?.[0]?.data ?? []).map((resource) => resource.id);
      return this.workTimeSchedules.loadSchedules(employeeIds).pipe(
        map((schedules) => {
          this.workTimesByEmployee = schedules;
          return [...resources];
        }),
        startWith(resources)
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  group: Group = {
    resources: ['Ressources'],
    orientation: 'vertical',
  };

  public weekStart: Day = Day.Monday;

  public selectedDate: Date = new Date();

  get RessourceTypes(){
    return RessourceType
  }

  ngOnInit(): void {
    this.absenceGroupStore.initRessources();
  }

  setSlotBackgroundColor = (args: SlotClassArgs) => {
    const classes: string[] = [];

    const activeHoliday = this.holidays.filter(
      (holiday) => holiday.start.toDateString() == args.start.toDateString()
    );
    if (Array.isArray(activeHoliday) && activeHoliday.length) {
      classes.push('holiday');
    }

    const employeeId = args.resources?.[0]?.id;
    if (
      employeeId &&
      this.workTimeSchedules.isNonWorkingSlot(this.workTimesByEmployee.get(employeeId), args)
    ) {
      classes.push('non-working-time');
    }

    return classes;
  };

  onDateChange(event: DateChangeEvent): void {
    this.absenceGroupStore.loadScheudler(event)
  }

  setEventClass = (args: EventStyleArgs) => {
    const event: ISchedulerEvent = args.event;
    const status = event.dataItem.absenceState;

    if (status === AbsenceState.Open) {
      return "open";
    }

    if (status === AbsenceState.Accepted) {
      return "accepted";
    }

    if (status === AbsenceState.Declined) {
      return "declined";
    }
  };
}
